import NavigationSidebar from '../common/NavigationSidebar';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getSidebarLogo } from '../../utils/logoUtils';
import { logoutApi } from '../../services/auth/authService';
import { revokeCurrentWebPushToken } from '../../services/webPush/firebaseWebPush';
import { usePermissions } from '../../context/PermissionsContext';
import { clearAllLocalLogoutData } from '../../utils/logoutCleanup';
import { isFeedFeatureEnabled } from '../../utils/featureFlags';

function SideBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { permissions } = usePermissions();

  // State for mobile sidebar visibility
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  // Track if we're on mobile or not
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check screen size on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-hide sidebar when resizing to desktop
      if (window.innerWidth >= 768) {
        setShowMobileSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile sidebar
  const toggleMobileSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar);
  };

  const isActive = (path) => {
    // For courses, check if the current path starts with /admin/courses or contains analytics
    if (path === '/admin/courses') {
      return (location.pathname.startsWith('/admin/courses') ||
              location.pathname.includes('/analytics/') ||
              location.pathname.includes('CourseAnalyticsSurveyDetails') ||
              location.pathname.includes('CourseAnalyticsAssessmentDetails') ||
              location.pathname.includes('edit-create')) ? 'active' : '';
    }
    // For classroom, check if the path starts with /admin/classrooms
    if (path === '/admin/classrooms') {
      return (location.pathname.startsWith('/admin/classrooms') ||
              location.pathname.includes('classroom-dashboard') ||
              location.pathname.includes('ClassroomAssignmentDetails') ||
              location.pathname.includes('ClassroomAssessmentDetails') ||
              location.pathname.includes('/trainees/')) ? 'active' : '';
    }
    // For trainees, check if the path starts with /admin/trainees or includes traineesAnalytics
    if (path === '/admin/trainees') {
      return (location.pathname.startsWith('/admin/trainees') ||
              location.pathname.includes('traineesAnalytics')) ? 'active' : '';
    }
    // For other paths, exact match
    return location.pathname === path ? 'active' : '';
  };

  const handleLinkClick = () => {
    // Close sidebar on mobile when clicking a link
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await revokeCurrentWebPushToken();
      // Optionally call logout API
      await logoutApi();

      // Clear all local/browser data for the signed-in user
      await clearAllLocalLogoutData();

      // Redirect to login page
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
      await clearAllLocalLogoutData();
      navigate('/');
    }
  };

  // Mobile toggle button to be rendered in the layout

  // Standard sidebar content
  const SidebarContent = () => (
    <div className={"tw-bg-[#fff] tw-h-[calc(100vh_-_78px)] tw-overflow-y-auto tw-w-full [&::-webkit-scrollbar]:tw-w-[5px] [&::-webkit-scrollbar-track]:tw-bg-[transparent] [&::-webkit-scrollbar-thumb]:tw-bg-[rgba(0,_0,_0,_0.1)] [&::-webkit-scrollbar-thumb]:tw-rounded-[10px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[rgba(0,_0,_0,_0.2)] sidebar-nav tw-py-2"}>
      {permissions.dashboard_management_view && (
        <Link
          to="/admin/dashboard"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/dashboard") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:grid-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Dashboard</span>
        </Link>
      )}

      {permissions.course_management_view && (
        <Link
          to="/admin/courses"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/courses") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:book-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Courses</span>
        </Link>
      )}

      {permissions.classroom_management_view && (
        <Link
          to="/admin/classrooms"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/classrooms") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:video-person-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Classrooms</span>
        </Link>
      )}

      {permissions.course_approval_workflow_view && (
        <Link
          to="/admin/approvalRequest"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/approvalRequest") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:clipboard-task-list-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Approval Requests</span>
        </Link>
      )}

      {permissions.certificate_management_view && (
        <Link
          to="/admin/certificates"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/certificates") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:certificate-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Certificates</span>
        </Link>
      )}

      {permissions.trainee_management_view && (
        <Link
          to="/admin/trainees"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/trainees") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:people-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Trainees</span>
        </Link>
      )}

      {permissions.roles_and_access_user_view && (
        <Link
          to="/admin/roles-and-access"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/roles-and-access") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:shield-lock-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Roles & Access</span>
        </Link>
      )}

      {permissions.question_bank_management_view && (
        <Link
          to="/admin/question-bank"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/question-bank") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:book-question-mark-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Question Bank</span>
        </Link>
      )}

      {isFeedFeatureEnabled() && (
        <Link
          to="/admin/feed"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/feed") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:news-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Feed</span>
        </Link>
      )}

      {permissions.setting_management_view && (
        <Link
          to="/admin/settings"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/admin/settings") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:settings-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Settings</span>
        </Link>
      )}

    </div>
  );

  const logoutButton = (
    <button
      type="button"
      className={"tw-flex tw-w-full tw-items-center tw-gap-4 tw-border-0 tw-rounded-[.5625rem] tw-bg-[transparent] tw-p-[.6875rem_.875rem] tw-text-[#e11d48] tw-text-[.875rem] tw-font-semibold tw-text-left [transition:background-color_.15s,_color_.15s] [&:hover:not(:disabled)]:tw-bg-[#fff1f2] [&:hover:not(:disabled)]:tw-text-[#be123c] [&:focus-visible]:[outline:2px_solid_#e11d48] [&:focus-visible]:[outline-offset:2px] [&:disabled]:tw-cursor-wait [&:disabled]:tw-opacity-[.65] workspace-logout-button"}
      onClick={handleLogout}
      disabled={isLoggingOut}
      aria-busy={isLoggingOut}
    >
      <Icon icon="fluent:sign-out-24-regular" width="22" height="22" aria-hidden="true" />
      <span>{isLoggingOut ? 'Signing out…' : 'Logout'}</span>
    </button>
  );

  const sidebarLogo = getSidebarLogo();

  return <NavigationSidebar isMobile={isMobile} open={showMobileSidebar} onToggle={toggleMobileSidebar} onClose={() => setShowMobileSidebar(false)} logo={sidebarLogo} home="/admin/dashboard" footer={logoutButton}><SidebarContent /></NavigationSidebar>;
}

export default SideBar;
